// ParentComponent.js
import React, { useContext, useEffect, useRef, useState, useMemo } from 'react'
import {
    useTable,
    useSortBy,
    usePagination,
    useFilters,
    useRowSelect,
} from 'react-table'
import { IconContext } from 'react-icons'
import { FaAngleUp, FaAngleDown } from 'react-icons/fa'
import { matchSorter } from 'match-sorter'
import { TableContext } from './table/TableContext'
import { SimpleSpinner } from '../../elem/Spinner'
import NewUploadButton from './NewUploadButton'
import { DataContext } from './DataContext'
import BulkSubmitButton from './table/BulkSubmitButton'
import { useLocation } from 'react-router-dom'
import Pagination from '../../elem/table/shared/Pagination'
import {
    getInProgressColumns,
    getSubmittedColumns,
    parseColumns,
} from '../../../utils/submissions/table'
import { UserContext } from '../../wrappers/UserContext'
import { isReviewerRole } from '../../../utils/user/permissions'
import ConfirmDelete from './table/ConfirmDelete'
import NoDataComponent from './table/NoDataComponent'
import {
    DefaultColumnFilter,
    fuzzyTextFilterFn,
    getHeaderProps,
    IndeterminateCheckbox,
} from '../../elem/table/SimpleTable'

const TableDisplay = ({
    getTableProps,
    headerGroups,
    getTableBodyProps,
    rows,
    prepareRow,
    selectable,
    sortable,
    filterable,
    columnsCount,
}) => {
    return (
        <div className="gridWrapper">
            <table
                {...getTableProps()}
                className="table is-fullwidth is-striped"
            >
                <thead
                    className="sticky-top"
                    style={{ backgroundColor: 'white' }}
                >
                    {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                                <th
                                    {...column.getHeaderProps()}
                                    className="is-size-7 vertical-align"
                                >
                                    <div className="explorerTableHeader simpleTableHeader">
                                        <div
                                            className="nameAndSort"
                                            {...getHeaderProps(
                                                column,
                                                sortable
                                            )}
                                        >
                                            {' '}
                                            {column.render('Header')}
                                            {sortable && column.isSorted && (
                                                <span className="explorerTableArrow simpleTableArrow">
                                                    <IconContext.Provider
                                                        value={{
                                                            className:
                                                                'has-text-link',
                                                            size: '1.1rem',
                                                        }}
                                                    >
                                                        {column.isSortedDesc ? (
                                                            <FaAngleDown />
                                                        ) : (
                                                            <FaAngleUp />
                                                        )}
                                                    </IconContext.Provider>
                                                </span>
                                            )}
                                        </div>
                                        {filterable && column.canFilter && (
                                            <div className="filter">
                                                {column.render('Filter')}
                                            </div>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {rows.length > 0 ? (
                        rows.map(row => {
                            prepareRow(row)
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map(cell => (
                                        <td {...cell.getCellProps()}>
                                            {cell.render('Cell')}
                                        </td>
                                    ))}
                                </tr>
                            )
                        })
                    ) : (
                        <tr>
                            <td
                                colSpan={columnsCount}
                                className="has-text-centered p-4"
                            >
                                No data available
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    )
}

const tabNameToStatus = {
    submitted: 'Submitted',
    approved: 'Approved',
    denied: 'Denied',
}

const selectableTabs = ['submitted', 'inProgress']

const SubmissionTabs = ({ onChange, active }) => (
    <div className="tabs is-large">
        <ul>
            <li
                className={`${active === 'inProgress' ? 'is-active' : ''}`}
                onClick={() => onChange('inProgress')}
            >
                <span className="link">Forms In Progress</span>
            </li>
            <li
                className={`${active === 'submitted' ? 'is-active' : ''}`}
                onClick={() => onChange('submitted')}
            >
                <span className="link">Submitted Forms</span>
            </li>
            <li
                className={`${active === 'approved' ? 'is-active' : ''}`}
                onClick={() => onChange('approved')}
            >
                <span className="link">Approved Forms</span>
            </li>
            <li
                className={`${active === 'denied' ? 'is-active' : ''}`}
                onClick={() => onChange('denied')}
            >
                <span className="link">Denied Forms</span>
            </li>
            <li
                className={`${active === 'inactives' ? 'is-active' : ''}`}
                onClick={() => onChange('inactives')}
            >
                <span className="link">Inactive CH#s</span>
            </li>
        </ul>
        <NewUploadButton />
    </div>
)

export default () => {
    const { state: locationState } = useLocation()
    const topRef = useRef(null)
    const sectionRef = useRef(null)
    const [topHeight, setTopHeight] = useState(0)

    const {
        uploads,
        setSelectedUploads,
        setConfirmDeleteModalData,
        deleteFunction,
    } = useContext(TableContext)
    const { roles } = useContext(UserContext)
    const {
        initializeState,
        setActiveAgency,
        setFacilityID,
        setErrorState,
    } = useContext(DataContext)

    const [activeTab, setActiveTab] = useState('inProgress')
    const isReviewer = useMemo(() => isReviewerRole(roles), [roles])

    const filterTypes = useMemo(
        () => ({
            fuzzyText: fuzzyTextFilterFn,
        }),
        []
    )

    const defaultColumn = useMemo(
        () => ({
            Filter: DefaultColumnFilter,
        }),
        []
    )

    const currentData = useMemo(() => {
        if (!uploads) return []

        if (activeTab === 'inProgress') {
            return uploads.incomplete || []
        } else if (activeTab === 'inactives') {
            return uploads.inactives || []
        } else {
            return (uploads.complete || []).filter(
                upload => upload.Status === tabNameToStatus[activeTab]
            )
        }
    }, [uploads, activeTab])

    const currentColumns = useMemo(() => {
        let columns = []
        if (activeTab === 'inProgress') {
            columns = getInProgressColumns(currentData, setConfirmDeleteModalData)
        } else if (activeTab === 'inactives') {
            columns = parseColumns(currentData, ['Deactivation Date'])
        } else {
            columns = getSubmittedColumns(currentData, isReviewer, activeTab)
            if (activeTab === 'submitted') {
                columns = columns.filter(x => x.id !== 'Approved / Denied Date')
            }
        }
        return columns
    }, [activeTab, currentData, isReviewer, setConfirmDeleteModalData])

    const tableInstance = useTable(
        {
            columns: currentColumns,
            data: currentData,
            defaultColumn,
            filterTypes,
            autoResetSelectedRows: false,
            initialState: {
                pageIndex: 0,
                pageSize: 50,
            },
        },
        useFilters,
        useSortBy,
        usePagination,
        useRowSelect,
        hooks => {
            if (selectableTabs.includes(activeTab)) {
                hooks.visibleColumns.push(columns => [
                    {
                        id: 'selection',
                        Header: ({ getToggleAllRowsSelectedProps }) => {
                            const headerProps = getToggleAllRowsSelectedProps()
                            return (
                                <div>
                                    <IndeterminateCheckbox {...headerProps} />
                                </div>
                            )
                        },
                        Cell: ({ row }) => (
                            <div>
                                <IndeterminateCheckbox
                                    {...row.getToggleRowSelectedProps()}
                                />
                            </div>
                        ),
                    },
                    ...columns,
                ])
            }
        }
    )

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        state,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        toggleAllRowsSelected,
    } = tableInstance
    const { selectedRowIds, filters } = state

    useEffect(() => {
        if (
            selectableTabs.includes(activeTab) &&
            setSelectedUploads &&
            selectedRowIds
        ) {
            const selectedIdx = Object.keys(selectedRowIds)
            const associatedRows = currentData.filter((x, idx) =>
                selectedIdx.includes(idx.toString())
            )
            setSelectedUploads(associatedRows)
        }
    }, [selectedRowIds, setSelectedUploads, currentData, activeTab])

    useEffect(() => {
        if (selectableTabs.includes(activeTab)) {
            toggleAllRowsSelected(false)
        }
    }, [filters, currentColumns, activeTab, toggleAllRowsSelected])

    useEffect(() => {
        setActiveAgency(null)
        initializeState({})
        setFacilityID(null)
        setErrorState({})
        setActiveTab(
            locationState && locationState.from
                ? locationState.from
                : 'inProgress'
        )
    }, [])

    useEffect(() => {
        const updateHeight = () => {
            if (topRef.current) {
                const height = topRef.current.getBoundingClientRect().height
                setTopHeight(height)
            }
        }

        updateHeight()
        const resizeObserver = new ResizeObserver(updateHeight)

        if (topRef.current) {
            resizeObserver.observe(topRef.current)
        }

        window.addEventListener('resize', updateHeight)

        return () => {
            resizeObserver.disconnect()
            window.removeEventListener('resize', updateHeight)
        }
    }, [])

    useEffect(() => {
        if (sectionRef.current) {
            sectionRef.current.style.maxHeight = `calc(100vh - ${topHeight}px - 2.3rem)`
        }
    }, [topHeight])

    const loading = !uploads

    return (
        <div className="container uploads">
            <ConfirmDelete deleteFunction={deleteFunction} />

            <div ref={topRef}>
                <SubmissionTabs onChange={setActiveTab} active={activeTab} />
                <div className="bulkSubmitWrapper">
                    <div style={{ maxWidth: 'fit-content' }}>
                        {activeTab === 'inProgress' ? (
                            <BulkSubmitButton action="Submit" />
                        ) : activeTab === 'submitted' && isReviewer ? (
                            <div className="columns m-1">
                                <BulkSubmitButton action="Approve" />
                                <BulkSubmitButton action="Deny" />
                            </div>
                        ) : null}
                    </div>
                    {!loading && (
                        <Pagination
                            extraClass="submissionsPagination"
                            backgroundColorClass="background-2"
                            selectDropDownCaratClass="carat-class-2"
                            gotoPage={gotoPage}
                            previousPage={previousPage}
                            canPreviousPage={canPreviousPage}
                            nextPage={nextPage}
                            canNextPage={canNextPage}
                            pageCount={pageCount}
                            resultsCount={currentData.length}
                            pageOptions={pageOptions}
                            pageIndex={(state && state.pageIndex) || 0}
                            pageSize={(state && state.pageSize) || 50}
                            setPageSize={setPageSize}
                        />
                    )}
                </div>
            </div>

            <div ref={sectionRef}>
                {loading ? (
                    <SimpleSpinner />
                ) : (
                    <div className="submissionsTableWrapper">
                        {!currentData || !currentData.length ? (
                            <NoDataComponent
                                message={`There are no ${
                                    activeTab === 'inProgress'
                                        ? 'Forms In Progress'
                                        : `${tabNameToStatus[activeTab]} Forms`
                                } to display.`}
                            />
                        ) : null}
                        <div
                            className={`${
                                !currentData || !currentData.length
                                    ? 'is-hidden'
                                    : ''
                            }`}
                        >
                            <TableDisplay
                                getTableProps={getTableProps}
                                headerGroups={headerGroups}
                                getTableBodyProps={getTableBodyProps}
                                rows={page}
                                prepareRow={prepareRow}
                                selectable={selectableTabs.includes(activeTab)}
                                sortable={true}
                                filterable={true}
                                columnsCount={
                                    currentColumns.length +
                                    (selectableTabs.includes(activeTab) ? 1 : 0)
                                }
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}